import { FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { AccountFormData } from '@/types';
import { Control, FieldErrors } from 'react-hook-form';
import { FormFieldWrapper } from '../FormFieldWrapper';

interface FormOptions {
  companyTypes: Array<{ value: string | number; label: string }>;
  languages: Array<{ value: string | number; label: string }>;
  countries: Array<{ value: string | number; label: string }>;
  industries: Array<{ value: string | number; label: string }>;
  cantons: Array<{ value: string | number; label: string }>;
  postalCodes: Array<{ value: string | number; label: string }>;
  cities: Array<{ value: string | number; label: string }>;
}

interface LoadingState {
  options: boolean;
  postalCodes: boolean;
  cities: boolean;
}

interface AccountGeneralInfoFormFieldsProps {
  control: Control<AccountFormData>;
  errors: FieldErrors<AccountFormData>;
  formOptions: FormOptions;
  isLoading: LoadingState;
  tForms: (key: string) => string;
  watchedCountry?: string;
}

export function AccountGeneralInfoFormFields({
  control,
  formOptions,
  isLoading,
  tForms,
  watchedCountry,
}: AccountGeneralInfoFormFieldsProps) {
  return (
    <div className='space-y-6'>
      {/* Row 1: Company Type & Language */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name='companyType'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.companyType')}
              name='companyType'
              required
              tooltip={tForms('tooltips.companyType')}
              error={fieldState.error?.message}
            >
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isLoading.options}
              >
                <SelectTrigger
                  className='min-h-[44px] w-full'
                  aria-describedby='companyType-error'
                >
                  <SelectValue
                    placeholder={tForms('placeholders.selectOption')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {isLoading.options ? (
                    <div className='p-2'>
                      <Skeleton className='h-8 w-full' />
                    </div>
                  ) : (
                    formOptions.companyTypes.map(option => (
                      <SelectItem
                        key={option.value}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name='language'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.language')}
              name='language'
              required
              tooltip={tForms('tooltips.language')}
              error={fieldState.error?.message}
            >
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isLoading.options}
              >
                <SelectTrigger className='min-h-[44px] w-full'>
                  <SelectValue
                    placeholder={tForms('placeholders.selectOption')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {isLoading.options ? (
                    <div className='p-2'>
                      <Skeleton className='h-8 w-full' />
                    </div>
                  ) : (
                    formOptions.languages.map(option => (
                      <SelectItem
                        key={option.value}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </FormFieldWrapper>
          )}
        />
      </div>

      {/* Row 2: Company Name & Name Addition */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name='name'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.name')}
              name='name'
              required
              tooltip={tForms('tooltips.companyName')}
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('placeholders.companyName')}
                className='min-h-[44px]'
                aria-describedby='name-error'
                autoComplete='organization'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name='nameAddition'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.nameAddition')}
              name='nameAddition'
              tooltip={tForms('tooltips.nameAddition')}
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('placeholders.nameAddition')}
                className='min-h-[44px]'
                autoComplete='off'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      {/* Row 3: Country */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name='country'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.country')}
              name='country'
              required
              tooltip={tForms('tooltips.country')}
              error={fieldState.error?.message}
            >
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isLoading.options}
              >
                <SelectTrigger className='min-h-[44px] w-full'>
                  <SelectValue
                    placeholder={tForms('placeholders.selectOption')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {isLoading.options ? (
                    <div className='p-2'>
                      <Skeleton className='h-8 w-full' />
                    </div>
                  ) : (
                    formOptions.countries.map(option => (
                      <SelectItem
                        key={option.value}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </FormFieldWrapper>
          )}
        />
        {/* Empty space for alignment */}
        <div></div>
      </div>

      {/* Row 4: Street & Street Number */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name='street'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.street')}
              name='street'
              required
              tooltip={tForms('tooltips.street')}
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('placeholders.streetAddress')}
                className='min-h-[44px]'
                autoComplete='street-address'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name='streetNo'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.streetNo')}
              name='streetNo'
              required
              tooltip={tForms('tooltips.streetNo')}
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('placeholders.streetNumber')}
                className='min-h-[44px]'
                autoComplete='address-line2'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      {/* Row 5: P.O. Box & Postal Code */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name='poBox'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.postBox')}
              name='poBox'
              tooltip={tForms('tooltips.postBox')}
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('placeholders.postBoxNumber')}
                className='min-h-[44px]'
                autoComplete='off'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name='postalCode'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.zipCode')}
              name='postalCode'
              required
              tooltip={tForms('tooltips.postalCode')}
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                type='text'
                placeholder={tForms('placeholders.enterZipCode')}
                className='min-h-[44px] w-full'
                disabled={isLoading.postalCodes || !watchedCountry}
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      {/* Row 6: City & Canton */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name='city'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.city')}
              name='city'
              required
              tooltip={tForms('tooltips.city')}
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                type='text'
                placeholder={tForms('placeholders.enterCity')}
                className='min-h-[44px] w-full'
                disabled={isLoading.cities}
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name='canton'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.canton')}
              name='canton'
              required={watchedCountry === 'switzerland'}
              tooltip={tForms('tooltips.canton')}
              error={fieldState.error?.message}
            >
              <Select
                value={field.value || ''}
                onValueChange={field.onChange}
                disabled={isLoading.options || watchedCountry !== 'switzerland'}
              >
                <SelectTrigger className='min-h-[44px] w-full'>
                  <SelectValue
                    placeholder={
                      watchedCountry === 'switzerland'
                        ? tForms('placeholders.selectOption')
                        : tForms('placeholders.selectCountryFirst')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {isLoading.options ? (
                    <div className='p-2'>
                      <Skeleton className='h-8 w-full' />
                    </div>
                  ) : watchedCountry === 'switzerland' ? (
                    formOptions.cantons.map(option => (
                      <SelectItem
                        key={option.value}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))
                  ) : (
                    <div className='p-2 text-sm text-muted-foreground'>
                      {tForms('placeholders.selectCountryFirst')}
                    </div>
                  )}
                </SelectContent>
              </Select>
            </FormFieldWrapper>
          )}
        />
      </div>

      {/* Row 7: Website & Industry */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name='website'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.website')}
              name='website'
              tooltip={tForms('tooltips.website')}
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                type='url'
                placeholder={tForms('placeholders.websiteUrl')}
                className='min-h-[44px]'
                autoComplete='url'
                inputMode='url'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name='industry'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.industry')}
              name='industry'
              required
              tooltip={tForms('tooltips.industry')}
              error={fieldState.error?.message}
            >
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isLoading.options}
              >
                <SelectTrigger className='min-h-[44px] w-full'>
                  <SelectValue
                    placeholder={tForms('placeholders.selectOption')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {isLoading.options ? (
                    <div className='p-2'>
                      <Skeleton className='h-8 w-full' />
                    </div>
                  ) : (
                    formOptions.industries.map(option => (
                      <SelectItem
                        key={option.value}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </FormFieldWrapper>
          )}
        />
      </div>

      {/* Row 8: Commercial Register Number */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name='commercialRegisterNumber'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('fields.commercialRegisterNumber')}
              name='commercialRegisterNumber'
              required
              tooltip={tForms('tooltips.commercialRegisterNumber')}
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('placeholders.commercialRegister')}
                className='min-h-[44px]'
                autoComplete='off'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />
        {/* Empty space for alignment */}
        <div></div>
      </div>
    </div>
  );
}
